/**
 * Hero Block Dynamic Aspect Ratio Handler
 * 
 * For hero blocks with videos (Wistia and iframe),
 * dynamically updates the --container-aspect-ratio CSS variable
 * based on the hero element's actual rendered dimensions.
 * This ensures videos fill the container without borders.
 */

class HeroBlockManager {
	constructor() {
		this.videoHeroes = [];
		this.resizeTimeout = null;
		this.init();
	}

	init() {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.onReady());
		} else {
			this.onReady();
		}
	}

	onReady() {
		this.videoHeroes = Array.from(
			document.querySelectorAll('.block-hero')
		).filter(hero => hero.querySelector('.block-hero__video wistia-player, .block-hero__video iframe'));

		if (this.videoHeroes.length > 0) {
			this.videoHeroes.forEach(hero => this.updateHeroAspectRatio(hero));

			window.addEventListener('resize', () => this.handleResize());

			window.addEventListener('orientationchange', () => {
				setTimeout(() => {
					this.videoHeroes.forEach(hero => this.updateHeroAspectRatio(hero));
				}, 100);
			});

			if (typeof ResizeObserver !== 'undefined') {
				this._heroResizeObserver = new ResizeObserver(() => {
					if (this.resizeTimeout) {
						clearTimeout(this.resizeTimeout);
					}
					this.resizeTimeout = setTimeout(() => {
						this.videoHeroes.forEach(hero => this.updateHeroAspectRatio(hero));
					}, 50);
				});
				this.videoHeroes.forEach(hero => this._heroResizeObserver.observe(hero));
			}
		}

		this.initVimeoReadyDetection();
	}

	/**
	 * Handle window resize with debouncing
	 */
	handleResize() {
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}

		this.resizeTimeout = setTimeout(() => {
			this.videoHeroes.forEach(hero => this.updateHeroAspectRatio(hero));
		}, 150);
	}

	/**
	 * Detect when Vimeo iframes are ready to play and swap from thumbnail to video.
	 * Only applies to containers with a thumbnail (--has-thumbnail class).
	 * Uses iframe load + timeout as the primary approach, with Vimeo postMessage as a faster path.
	 */
	initVimeoReadyDetection() {
		const vimeoContainers = document.querySelectorAll('.block-hero__video--has-thumbnail');

		if (vimeoContainers.length === 0) {
			return;
		}

		this.vimeoIframeMap = new Map();

		vimeoContainers.forEach(container => {
			const iframe = container.querySelector('iframe');
			if (!iframe) return;

			const markReady = () => {
				if (!container.classList.contains('block-hero__video--ready')) {
					container.classList.add('block-hero__video--ready');
				}
			};

			const setupPlayer = () => {
				const cw = iframe.contentWindow;
				if (!cw) return;

				this.vimeoIframeMap.set(cw, container);

				// Use targetOrigin '*' so we don't throw when the iframe document is not yet
				// player.vimeo.com (e.g. about:blank inherits the parent origin until load).
				// The Vimeo player still receives the message once it is the embedded document.
				try {
					cw.postMessage(
						JSON.stringify({ method: 'addEventListener', value: 'play' }),
						'*'
					);
				} catch (e) {
					// fall through to timeout
				}

				setTimeout(markReady, 2500);
			};

			iframe.addEventListener('load', () => {
				setupPlayer();
				const hero = container.closest('.block-hero');
				if (hero) {
					this.updateHeroAspectRatio(hero);
				}
			});

			// The iframe src is set in server-rendered HTML with loading="eager",
			// so the load event may have already fired before this JS runs.
			// Detect that case and run setup immediately.
			try {
				const doc = iframe.contentDocument || iframe.contentWindow?.document;
				if (doc && doc.readyState === 'complete') {
					setupPlayer();
				}
			} catch (e) {
				// Cross-origin: can't inspect readyState. Fall back to a
				// safety-net timeout that reveals the video even if the load
				// event was missed entirely.
				setTimeout(markReady, 4000);
			}
		});

		// Listen for Vimeo postMessage events (faster path if it fires)
		window.addEventListener('message', (event) => {
			if (!event.origin || !event.origin.includes('vimeo.com')) return;

			let data;
			try {
				data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
			} catch (e) {
				return;
			}

			if (data.event === 'play' || data.event === 'playProgress' || data.event === 'bufferend') {
				const container = this.vimeoIframeMap.get(event.source);
				if (container && !container.classList.contains('block-hero__video--ready')) {
					container.classList.add('block-hero__video--ready');
				}
			}
		});
	}

	/**
	 * Update --container-aspect-ratio from the hero's actual rendered size.
	 * @param {HTMLElement} hero - The hero block element
	 */
	updateHeroAspectRatio(hero) {
		const rect = hero.getBoundingClientRect();

		if (rect.width === 0 || rect.height === 0) return;

		const aspectRatio = rect.width / rect.height;
		hero.style.setProperty('--container-aspect-ratio', aspectRatio.toFixed(3));

		requestAnimationFrame(() => {
			requestAnimationFrame(() => this.applyVimeoAlignment(hero));
		});
	}

	/**
	 * Resolve background align slug from hero modifier classes.
	 * @param {HTMLElement} hero
	 * @returns {string}
	 */
	getHeroBackgroundAlign(hero) {
		for (const cls of hero.classList) {
			if (cls.startsWith('block-hero--bg-align-')) {
				return cls.slice('block-hero--bg-align-'.length);
			}
		}
		return 'center';
	}

	/**
	 * Pixel-position the oversized Vimeo iframe to match background alignment.
	 * CSS anchor rules alone cannot crop Vimeo's internal letterboxing reliably.
	 * @param {HTMLElement} hero
	 */
	applyVimeoAlignment(hero) {
		const container = hero.querySelector('.block-hero__video--vimeo');
		const iframe = container?.querySelector('iframe');
		if (!container || !iframe) return;

		const cw = container.clientWidth;
		const ch = container.clientHeight;
		if (!cw || !ch) return;

		const iw = iframe.offsetWidth;
		const ih = iframe.offsetHeight;
		if (!iw || !ih) return;

		const overflowX = iw - cw;
		const overflowY = ih - ch;
		const align = this.getHeroBackgroundAlign(hero);

		let top;
		let left;

		if (overflowX > 0 || overflowY > 0) {
			const cropX = Math.max(0, overflowX);
			const cropY = Math.max(0, overflowY);

			const cropPositions = {
				center: { top: -cropY / 2, left: -cropX / 2 },
				top: { top: 0, left: -cropX / 2 },
				'top-left': { top: 0, left: 0 },
				'top-right': { top: 0, left: -cropX },
				left: { top: -cropY / 2, left: 0 },
				right: { top: -cropY / 2, left: -cropX },
				bottom: { top: -cropY, left: -cropX / 2 },
				'bottom-left': { top: -cropY, left: 0 },
				'bottom-right': { top: -cropY, left: -cropX },
			};

			({ top, left } = cropPositions[align] || cropPositions.center);
		} else {
			const fitPositions = {
				center: { top: (ch - ih) / 2, left: (cw - iw) / 2 },
				top: { top: 0, left: (cw - iw) / 2 },
				'top-left': { top: 0, left: 0 },
				'top-right': { top: 0, left: cw - iw },
				left: { top: (ch - ih) / 2, left: 0 },
				right: { top: (ch - ih) / 2, left: cw - iw },
				bottom: { top: ch - ih, left: (cw - iw) / 2 },
				'bottom-left': { top: ch - ih, left: 0 },
				'bottom-right': { top: ch - ih, left: cw - iw },
			};

			({ top, left } = fitPositions[align] || fitPositions.center);
		}

		iframe.style.transform = 'none';
		iframe.style.right = 'auto';
		iframe.style.bottom = 'auto';
		iframe.style.top = `${top}px`;
		iframe.style.left = `${left}px`;
	}
}

// Initialize hero block manager
const heroBlockManager = new HeroBlockManager();

// Export for use in other modules if needed
export default HeroBlockManager;
